// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useQuery } from '@apollo/client';
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { TitleCard } from '../components/common/card';
import EmptyContainer from '../components/common/empty/EmptyContainer';
import { PageError } from '../components/common/error';
import { ALL_ITEMS, getNewSelectedValues, makeNameFilter } from '../components/common/filter';
import FiltersContainer from '../components/common/filter/FiltersContainer';
import { PageLoading } from '../components/common/loading';
import AddOrderDialog from '../components/order/AddOrderDialog';
import RecurringOrderTable from '../components/recurringOrder/RecurringOrderTable';
import RecurringOrderTableOptions from '../components/recurringOrder/RecurringOrderTableOptions';
import { useGating, useUserContext } from '../context/UserContext';
import { Box, Button, type SelectChangeEvent, Typography } from '../lib/base';
import { AddIcon } from '../lib/icons';
import { RECURRING_ORDERS_QUERY } from '../queries/RecurringOrderQueries';
import type { RecurringOrdersHomeQuery, RecurringOrdersHomeQueryVariables } from '../types/__generated__/graphql';
import { getTimeRanges } from '../utils/getTimeRanges';

export default React.memo(function RecurringOrders() {
  const { permissions, timezone } = useUserContext();
  const { canUseBasic } = useGating();
  const { todayStart } = getTimeRanges(timezone);
  const { data, loading, error, refetch } = useQuery<RecurringOrdersHomeQuery, RecurringOrdersHomeQueryVariables>(
    RECURRING_ORDERS_QUERY,
    {
      variables: { todayStart: todayStart.toString() },
    },
  );
  const [newRecurringOrderOpen, setNewRecurringOrderOpen] = React.useState(false);
  const [currentRecurringOrdersNameFilter, setCurrentRecurringOrdersNameFilter] = React.useState([ALL_ITEMS]);

  const recurringOrders = React.useMemo(() => data?.recurring_orders?.edges.map((edge) => edge.node) ?? [], [data]);

  const handleNameFilterChange = React.useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const prevValues = [...currentRecurringOrdersNameFilter];
      const selectedValues = e.target.value as string[];
      const newSelectedValues = getNewSelectedValues(prevValues, selectedValues);
      setCurrentRecurringOrdersNameFilter(newSelectedValues);
    },
    [currentRecurringOrdersNameFilter],
  );

  const nameFilter = React.useMemo(
    () =>
      makeNameFilter({
        value: currentRecurringOrdersNameFilter,
        onChangeValue: handleNameFilterChange,
        objects: recurringOrders,
        getName: (obj) => obj.customer,
      }),
    [currentRecurringOrdersNameFilter, handleNameFilterChange, recurringOrders],
  );

  const currentRecurringOrdersFiltersRow = React.useMemo(
    () => <FiltersContainer otherFilters={[nameFilter]} />,
    [nameFilter],
  );

  const filteredRecurringOrders = React.useMemo(
    () =>
      recurringOrders.filter((order) =>
        currentRecurringOrdersNameFilter.includes(ALL_ITEMS)
          ? true
          : currentRecurringOrdersNameFilter.includes(order.customer),
      ),
    [currentRecurringOrdersNameFilter, recurringOrders],
  );

  const userCanEditOrders = permissions.can_edit_orders;

  const content = React.useMemo(() => {
    if (error) {
      return <PageError error={error} />;
    }
    if (loading) {
      return <PageLoading />;
    }
    if (data == null || data.recurring_orders == null) {
      return <PageError />;
    }
    if (recurringOrders.length === 0) {
      return (
        <EmptyContainer
          title="You have no recurring orders!"
          description={'Click the "New Order +" button to start creating recurring orders.'}
        />
      );
    }

    return <RecurringOrderTable userCanEditRecurringOrders={userCanEditOrders} orders={filteredRecurringOrders} />;
  }, [loading, error, data, userCanEditOrders, filteredRecurringOrders, recurringOrders]);

  const handleNewOrderClick = React.useCallback(() => setNewRecurringOrderOpen(true), []);
  const handleDialogClose = React.useCallback(() => setNewRecurringOrderOpen(false), []);

  const options = React.useMemo(
    () =>
      userCanEditOrders ? (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
          <Button
            id="new-order-button"
            variant="contained"
            onClick={handleNewOrderClick}
            endIcon={<AddIcon sx={{ color: 'secondary.contrastText' }} />}
            color="secondary"
            disabled={!canUseBasic}
          >
            <Typography variant="body1" sx={{ color: 'secondary.contrastText' }}>
              New Order
            </Typography>
          </Button>
          <RecurringOrderTableOptions useArrowOnly />
        </Box>
      ) : (
        <RecurringOrderTableOptions />
      ),
    [userCanEditOrders, handleNewOrderClick, canUseBasic],
  );

  return (
    <>
      <Helmet>
        <title>{'Recurring Orders'}</title>
      </Helmet>
      <TitleCard
        title="Recurring Orders"
        rightElement={options}
        content={content}
        extraHeaderContent={currentRecurringOrdersFiltersRow}
      />
      {newRecurringOrderOpen && (
        <AddOrderDialog
          isForNewSingleOrder={false}
          isOpen={newRecurringOrderOpen}
          onClose={handleDialogClose}
          onSuccess={() => {
            void refetch();
            handleDialogClose();
          }}
        />
      )}
    </>
  );
});
